// v10/src/components/common/ExportReportModal.js
//
// Modal genérico reusable para exportar reportes en PDF / Excel. Lo consumen
// los tabs A6.1 (mantenimiento) y A8 (stock) en parte 1, y los tabs A9
// (combustible) y libro de vuelos del piloto en parte 2.
//
// Props:
//   isOpen           — bool
//   title            — string (título visible)
//   formats          — array, default ['pdf','xlsx']
//   periodOptions    — { monthPicker: bool, rangePicker: bool, allPicker: bool },
//                      default { monthPicker:true, rangePicker:true, allPicker:false }
//                      allPicker=true habilita un 3er tab "Histórico completo" sin
//                      selectores que envía { period_type: 'all' } (parte 2 — logbook).
//   onExport(params) — async fn que recibe { format, period_type, month?, year?, from?, to? }
//   onClose          — fn
//   darkMode         — bool
//
// El callback `onExport` es el que dispara la descarga vía reportsService.
// Este modal solo arma el body, muestra loader y maneja el toast.

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, FileSpreadsheet, X, Loader, Calendar, CalendarRange, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/useToast';

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const ExportReportModal = ({
  isOpen,
  title,
  formats = ['pdf', 'xlsx'],
  periodOptions = { monthPicker: true, rangePicker: true },
  onExport,
  onClose,
  darkMode,
}) => {
  const { t } = useTranslation('admin');
  const toast = useToast();

  const now = useMemo(() => new Date(), []);

  // Default: prioriza month → range → all (lo primero habilitado)
  const defaultPeriodType = periodOptions.monthPicker
    ? 'month'
    : periodOptions.rangePicker
      ? 'range'
      : 'all';
  const [periodType, setPeriodType] = useState(defaultPeriodType);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [from,  setFrom]  = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'xlsx' | null

  // Reset cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setPeriodType(defaultPeriodType);
      setDownloading(null);
    }
  }, [isOpen, defaultPeriodType]);

  // ESC para cerrar
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !downloading) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, downloading, onClose]);

  if (!isOpen) return null;

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgSoft    = darkMode ? 'rgba(255,255,255,0.7)'  : '#475569';
  const bgInput   = darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc';
  const bdInput   = darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0';

  const rangeValid = (() => {
    if (periodType !== 'range') return true;
    if (!from || !to) return false;
    return new Date(from) <= new Date(to);
  })();
  const monthValid = (() => {
    if (periodType !== 'month') return true;
    return Number.isInteger(month) && month >= 1 && month <= 12
        && Number.isInteger(year)  && year  >= 2000;
  })();
  const canSubmit = (rangeValid && monthValid && !downloading);

  const handleExport = async (format) => {
    if (!canSubmit) return;
    setDownloading(format);
    try {
      const params = { format };
      if (periodType === 'month') {
        params.period_type = 'month';
        params.month       = month;
        params.year        = year;
      } else if (periodType === 'range') {
        params.period_type = 'range';
        params.from        = from;
        params.to          = to;
      } else {
        // 'all' — histórico completo, sin month/year/from/to
        params.period_type = 'all';
      }
      await onExport?.(params);
      toast.success(t('report_download_success'));
      onClose?.();
    } catch (err) {
      // Si el backend devuelve un blob de error, leerlo como JSON sería ideal.
      // Por ahora caemos al mensaje genérico (el backend responde 4xx con JSON).
      console.error('Export error:', err);
      toast.error(t('report_download_error'));
    } finally {
      setDownloading(null);
    }
  };

  // Lista de años: año actual y 5 hacia atrás
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  // Tabs habilitadas según props (en orden fijo: month → range → all)
  const enabledTabs = [];
  if (periodOptions.monthPicker)        enabledTabs.push({ id: 'month', label: t('report_period_month'), Icon: Calendar });
  if (periodOptions.rangePicker)        enabledTabs.push({ id: 'range', label: t('report_period_range'), Icon: CalendarRange });
  if (periodOptions.allPicker === true) enabledTabs.push({ id: 'all',   label: t('report_period_all'),   Icon: History });

  // Si solo hay UN período habilitado no mostramos las tabs.
  const showTabs = enabledTabs.length > 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 110,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
        }}
        onClick={downloading ? undefined : onClose}
      >
        <motion.div
          initial={{ scale: 0.92, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 20 }}
          style={{
            width: '100%', maxWidth: 480, borderRadius: 24,
            background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
            padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
            fontFamily: 'inherit',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 14,
              background: 'rgba(14,165,233,0.1)',
              border: '1px solid rgba(14,165,233,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FileText style={{ width: 22, height: 22, color: '#0ea5e9' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: fgPrimary }}>
                {title || t('report_modal_title_default')}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
                {t('report_modal_subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={!!downloading}
              aria-label={t('report_modal_close')}
              style={{
                background: 'transparent', border: 'none',
                cursor: downloading ? 'not-allowed' : 'pointer',
                padding: 6, color: fgMuted, display: 'flex',
              }}
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>

          {/* Tabs de período (solo si hay más de una opción habilitada) */}
          {showTabs && (
            <div style={{
              display: 'inline-flex', alignSelf: 'flex-start',
              padding: 4, borderRadius: 12, gap: 4, marginBottom: 16,
              background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
              border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
              width: '100%', boxSizing: 'border-box',
            }}>
              {enabledTabs.map((opt) => {
                const selected = opt.id === periodType;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPeriodType(opt.id)}
                    disabled={!!downloading}
                    style={{
                      flex: 1, display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', gap: 8,
                      padding: '8px 12px', borderRadius: 10, border: 'none',
                      cursor: downloading ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                      background: selected
                        ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white')
                        : 'transparent',
                      color: selected ? fgPrimary : fgMuted,
                      boxShadow: selected ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                      transition: 'all 0.18s',
                    }}
                  >
                    <opt.Icon style={{ width: 14, height: 14 }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Inputs según el período activo */}
          {periodType === 'all' ? (
            <div
              style={{
                padding: '14px 16px', marginBottom: 18, borderRadius: 14,
                background: darkMode ? 'rgba(14,165,233,0.08)' : 'rgba(14,165,233,0.06)',
                border: darkMode ? '1px solid rgba(14,165,233,0.25)' : '1px solid rgba(14,165,233,0.2)',
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <History style={{ width: 18, height: 18, color: '#0ea5e9', flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: 13, color: fgSoft, lineHeight: 1.4 }}>
                {t('report_period_all_description')}
              </p>
            </div>
          ) : periodType === 'month' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: fgSoft, marginBottom: 6 }}>
                  {t('report_period_month_label')}
                </label>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                  disabled={!!downloading}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: 12,
                    border: bdInput, background: bgInput,
                    color: fgPrimary, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                >
                  {MONTHS_ES.map((label, idx) => (
                    <option key={idx + 1} value={idx + 1}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: fgSoft, marginBottom: 6 }}>
                  {t('report_period_year_label')}
                </label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value, 10))}
                  disabled={!!downloading}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: 12,
                    border: bdInput, background: bgInput,
                    color: fgPrimary, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: fgSoft, marginBottom: 6 }}>
                  {t('report_period_from_label')}
                </label>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  disabled={!!downloading}
                  max={to || undefined}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: 12,
                    border: bdInput, background: bgInput,
                    color: fgPrimary, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: fgSoft, marginBottom: 6 }}>
                  {t('report_period_to_label')}
                </label>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  disabled={!!downloading}
                  min={from || undefined}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: 12,
                    border: bdInput, background: bgInput,
                    color: fgPrimary, fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
            </div>
          )}

          {!rangeValid && (
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
              {t('report_period_range_invalid')}
            </p>
          )}

          {/* Botones de formato */}
          <div style={{ display: 'flex', gap: 10 }}>
            <style>{`@keyframes report-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
            {formats.includes('pdf') && (
              <motion.button
                type="button"
                onClick={() => handleExport('pdf')}
                disabled={!canSubmit}
                whileHover={canSubmit ? { scale: 1.02 } : {}}
                whileTap={canSubmit ? { scale: 0.97 } : {}}
                style={{
                  flex: 1, padding: 13, borderRadius: 14, border: 'none',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  background: canSubmit
                    ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                    : (darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'),
                  color: canSubmit ? 'white' : (darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8'),
                  fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {downloading === 'pdf'
                  ? <Loader style={{ width: 16, height: 16, animation: 'report-spin 1s linear infinite' }} />
                  : <FileText style={{ width: 16, height: 16 }} />}
                {downloading === 'pdf' ? t('report_downloading') : t('report_download_pdf')}
              </motion.button>
            )}
            {formats.includes('xlsx') && (
              <motion.button
                type="button"
                onClick={() => handleExport('xlsx')}
                disabled={!canSubmit}
                whileHover={canSubmit ? { scale: 1.02 } : {}}
                whileTap={canSubmit ? { scale: 0.97 } : {}}
                style={{
                  flex: 1, padding: 13, borderRadius: 14, border: 'none',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  background: canSubmit
                    ? 'linear-gradient(135deg,#10b981,#059669)'
                    : (darkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'),
                  color: canSubmit ? 'white' : (darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8'),
                  fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {downloading === 'xlsx'
                  ? <Loader style={{ width: 16, height: 16, animation: 'report-spin 1s linear infinite' }} />
                  : <FileSpreadsheet style={{ width: 16, height: 16 }} />}
                {downloading === 'xlsx' ? t('report_downloading') : t('report_download_xlsx')}
              </motion.button>
            )}
          </div>

          <p style={{
            margin: '14px 0 0', fontSize: 11,
            color: darkMode ? 'rgba(255,255,255,0.45)' : '#94a3b8',
            textAlign: 'center', lineHeight: 1.5,
          }}>
            {t('report_modal_footer_hint')}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExportReportModal;
